import React, { useEffect, useRef, useState } from "react"

import "./dialer.css"
import I from "./icons"
import { DEMO_LISTS, DEMO_REP } from "./dummy-data"
import useVoiceSessionStore from "~/store/voice-session"
import useGlobalStore from "~/store/global"
import { useVoiceDevice } from "~/hooks/use-voice-device"
import { getVoiceNumbers, setCallHold } from "~/api/voice"

const COUNTRY_LABEL = { IN: "India", US: "United States", GB: "United Kingdom" }

function useHoldToggle(tokenInfo, callSidRef) {
  const [held, setHeld] = useState(false)
  const toggleHold = async () => {
    const next = !held
    setHeld(next)
    const sid = callSidRef.current
    if (!sid || !tokenInfo?.tenant_id) return
    try {
      await setCallHold(tokenInfo, sid, next)
    } catch {
      setHeld(!next)
    }
  }
  return [held, setHeld, toggleHold]
}

function dlInit(n) {
  return (n || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
}
function dlOutcomeLabel(outcome) {
  switch (outcome) {
    case "NO_ANSWER":
      return "No answer"
    case "FAILED":
      return "Call failed"
    case "COMPLETED":
      return "Call ended"
    default:
      return "Call ended"
  }
}
function dlFmt(sec) {
  const m = Math.floor(sec / 60),
    s = sec % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

function dlSummary(person) {
  const them = person.name.replace(/^Dr\.?\s+/i, "").split(/\s+/)[0]
  return {
    tldr: `${them} is under CFO pressure on unit cost from three overlapping stacks ahead of the renewal. Open to a working session with architects; wants cost-model options for week of the 21st.`,
    sentiment: "Positive",
    next: [
      "Send 2 meeting slots for week of the 21st",
      "Prepare consolidation cost model for the CFO",
      "Loop in Sanjay Rao (owns migration plan)"
    ]
  }
}

const CALL_DISPO = [
  { id: "meeting", label: "Meeting set", icon: "calendar", cls: "meet" },
  { id: "followup", label: "Follow up", icon: "reply", cls: "follow" },
  { id: "nextcall", label: "Next call", icon: "phone", cls: "next" },
  { id: "reachout", label: "Reach out", icon: "mail", cls: "reach" },
  { id: "none", label: "Not interested", icon: "close", cls: "none" }
]
const CALL_DISPO_LABEL = Object.fromEntries(
  CALL_DISPO.map((d) => [d.id, d.label])
)
const CALL_DISPO_CLS = Object.fromEntries(CALL_DISPO.map((d) => [d.id, d.cls]))
function clkFmtMeeting(at) {
  if (!at || !at.date) return ""
  const d = new Date(at.date + "T" + (at.time || "09:00"))
  const day = d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric"
  })
  return at.time ? day + " · " + at.time : day
}

function useDragOffset() {
  const [off, setOff] = useState({ x: 0, y: 0 })
  const start = useRef(null)
  const onDragStart = (e) => {
    if (e.button !== 0) return
    e.stopPropagation()
    start.current = { sx: e.clientX, sy: e.clientY, ox: off.x, oy: off.y }
    const move = (ev) =>
      setOff({
        x: start.current.ox + ev.clientX - start.current.sx,
        y: start.current.oy + ev.clientY - start.current.sy
      })
    const up = () => {
      window.removeEventListener("mousemove", move)
      window.removeEventListener("mouseup", up)
    }
    window.addEventListener("mousemove", move)
    window.addEventListener("mouseup", up)
  }
  return {
    style: { transform: `translate(${off.x}px, ${off.y}px)` },
    onDragStart
  }
}

function PersonDrawer({ person, onClose }) {
  if (!person) return null
  return (
    <div
      className="pstub-scrim"
      onClick={onClose}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="pstub"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="pstub-hd">
          <span className="clk-label">Full profile</span>
          <button className="pstub-x" onClick={onClose} aria-label="Close">
            {I.close({ size: 16 })}
          </button>
        </div>
        <span className="pstub-av">{dlInit(person.name)}</span>
        <div className="pstub-nm">{person.name}</div>
        <div className="pstub-role">
          {person.title}
          {person.account ? " · " + person.account.name : ""}
        </div>
        <div className="pstub-field">
          <div className="pstub-flabel">Phone</div>
          <div className="pstub-fval">
            {person.phone || "No number on file"}
          </div>
        </div>
        <div className="pstub-field">
          <div className="pstub-flabel">Account</div>
          <div className="pstub-fval">
            {person.account ? person.account.name : "—"}
          </div>
        </div>
        <div className="pstub-field">
          <div className="pstub-flabel">Talking points</div>
          <div className="pstub-fval">
            Under CFO pressure on unit cost; three overlapping stacks ahead of
            the renewal. Open to a working session with architects.
          </div>
        </div>
      </div>
    </div>
  )
}

function CallLock4L({
  person,
  durationSec,
  outcome,
  log,
  setLog,
  onProfile,
  onRedial,
  variant = "wide"
}) {
  const summary = dlSummary(person)
  const preset = (days, time) => {
    const d = new Date()
    d.setDate(d.getDate() + days)
    const p = (n) => String(n).padStart(2, "0")
    setLog((l) => ({
      ...l,
      meetingAt: {
        date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
        time
      }
    }))
  }
  const mAt = log.meetingAt || {}
  return (
    <div className={"clk clk-" + variant}>
      <div className="clk-hd">
        <span className="dlr-av sm">{dlInit(person.name)}</span>
        <div className="clk-id">
          <span className="clk-nm">{person.name}</span>
          <span className="clk-meta">
            {I.checkCircle({ size: 11 })}{" "}
            {outcome && outcome !== "COMPLETED" && outcome !== "ANSWERED"
              ? dlOutcomeLabel(outcome)
              : "Connected · " + dlFmt(Math.max(durationSec, 1))}
          </span>
        </div>
        {onProfile && (
          <button className="bdl-redial" onClick={onProfile}>
            {I.user({ size: 12 })} Profile
          </button>
        )}
        {onRedial && (
          <button className="bdl-redial" onClick={onRedial}>
            {I.reply({ size: 12 })} Redial
          </button>
        )}
      </div>
      <div className="clk-cols">
        <div className="clk-form">
          <div className="clk-label">What happened on the call?</div>
          <div className="clk-dispo">
            {CALL_DISPO.map((o) => (
              <button
                key={o.id}
                className={
                  "clk-dbtn is-" + o.cls + (log.dispo === o.id ? " is-on" : "")
                }
                onClick={() =>
                  setLog((l) => ({
                    ...l,
                    dispo: l.dispo === o.id ? "" : o.id
                  }))
                }
              >
                {I[o.icon]({ size: 14 })} {o.label}
              </button>
            ))}
          </div>
          {log.dispo === "meeting" && (
            <div className="clk-meeting">
              <div className="clk-meeting-t">
                {I.calendar({ size: 13 })} When's the meeting?
              </div>
              <div className="clk-meeting-row">
                <input
                  className="clk-dt"
                  type="date"
                  value={mAt.date || ""}
                  onChange={(e) =>
                    setLog((l) => ({
                      ...l,
                      meetingAt: { ...l.meetingAt, date: e.target.value }
                    }))
                  }
                />
                <input
                  className="clk-dt"
                  type="time"
                  value={mAt.time || ""}
                  onChange={(e) =>
                    setLog((l) => ({
                      ...l,
                      meetingAt: { ...l.meetingAt, time: e.target.value }
                    }))
                  }
                />
              </div>
              <div className="clk-presets">
                <button
                  className="clk-preset"
                  onClick={() => preset(1, "10:00")}
                >
                  Tomorrow · 10:00
                </button>
                <button
                  className="clk-preset"
                  onClick={() => preset(2, "14:00")}
                >
                  In 2 days · 14:00
                </button>
                <button
                  className="clk-preset"
                  onClick={() => preset(7, "11:00")}
                >
                  Next week · 11:00
                </button>
              </div>
              {mAt.date && (
                <div className="clk-meeting-confirm">
                  {I.check({ size: 12 })} Meeting set for{" "}
                  <b>{clkFmtMeeting(mAt)}</b>
                </div>
              )}
            </div>
          )}
          <div className="clk-label clk-label-mt">Notes</div>
          <textarea
            className="bdl-notes"
            rows={variant === "compact" ? 2 : 3}
            placeholder="Anything the AI summary should not miss…"
            value={log.notes}
            onChange={(e) => setLog((l) => ({ ...l, notes: e.target.value }))}
          />
        </div>
        <div className="bdl-aicard clk-ai">
          <div className="bdl-ai-t">
            {I.sparkle({ size: 12 })} AI summary{" "}
            <span className="bdl-ai-auto">auto-captured</span>
          </div>
          <p className="bdl-ai-tldr">{summary.tldr}</p>
          {variant !== "compact" && (
            <React.Fragment>
              <div className="bdl-ai-next-t">Next steps</div>
              <ul className="bdl-ai-next">
                {summary.next.map((n, i) => (
                  <li key={i}>
                    {I.check({ size: 11 })}
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </React.Fragment>
          )}
          <div className="bdl-ai-foot">
            {I.doc({ size: 11 })} Full transcript saved to timeline
          </div>
        </div>
      </div>
    </div>
  )
}

export function Dialer4L({ person, rep = DEMO_REP, onClose, onLogCall }) {
  const onFile = person.phone && /\d/.test(person.phone) ? person.phone : ""
  const [phase, setPhase] = useState("compose")
  const [number, setNumber] = useState(onFile)
  const [editNum, setEditNum] = useState(!onFile)
  const [minimized, setMinimized] = useState(false)
  const [sec, setSec] = useState(0)
  const [muted, setMuted] = useState(false)
  const drag = useDragOffset()
  const [showPad, setShowPad] = useState(false)
  const [pad, setPad] = useState("")
  const tRef = useRef(null)
  const {
    status: devStatus,
    makeCall,
    logCall,
    watchCallStatus,
    endSession
  } = useVoiceDevice()
  const [outcome, setOutcome] = useState(null)
  const callRef = useRef(null)
  const callSidRef = useRef(null)
  const tokenInfo = useGlobalStore((s) => s.tokenInfo)
  const [held, , toggleHold] = useHoldToggle(tokenInfo, callSidRef)
  useEffect(() => {
    callRef.current?.mute(muted)
  }, [muted])
  const [showProfile, setShowProfile] = useState(false)
  const [lock, setLock] = useState({ dispo: "", meetingAt: null, notes: "" })
  const dialable = number.replace(/\D/g, "").length >= 7

  useEffect(() => {
    if (phase !== "ringing") return
    setOutcome(null)
    let cancelled = false
    let sim = null
    ;(async () => {
      const call =
        devStatus === "ready"
          ? await makeCall(
              number,
              person?.location_country
                ? { calleeCountry: person.location_country }
                : undefined,
              person?.id
            )
          : null
      if (cancelled) return
      if (!call) {
        sim = setTimeout(() => setPhase("live"), 2200)
        return
      }
      callRef.current = call
      let stopWatching = () => {}
      call.on("accept", () => {
        callSidRef.current = call.parameters?.CallSid ?? null
        stopWatching = watchCallStatus(callSidRef.current, (status) => {
          setOutcome(status)
          if (status === "ANSWERED") setPhase("live")
        })
      })
      const end = () => {
        stopWatching()
        clearInterval(tRef.current)
        callRef.current = null
        setPhase("ended")
      }
      call.on("disconnect", end)
      call.on("cancel", end)
      call.on("reject", end)
      call.on("error", end)
    })()
    return () => {
      cancelled = true
      if (sim) clearTimeout(sim)
    }
  }, [phase, devStatus, makeCall, number, watchCallStatus])
  useEffect(() => {
    if (phase !== "live") return
    tRef.current = setInterval(() => setSec((s) => s + 1), 1000)
    return () => clearInterval(tRef.current)
  }, [phase])

  const hangUp = () => {
    clearInterval(tRef.current)
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    setPhase("ended")
  }
  const saveClose = () => {
    const dur = Math.max(sec, 1)
    logCall(callSidRef.current, {
      disposition: lock.dispo || undefined,
      notes: lock.notes || undefined,
      meetingAt: lock.meetingAt
    })
    endSession()
    if (onLogCall)
      onLogCall({
        id: "call-" + Date.now(),
        type: "call",
        dir: "out",
        outcome: "connected",
        durationSec: dur,
        duration: dlFmt(dur),
        when: "Just now",
        date: "Today",
        rep,
        disposition: lock.dispo,
        meetingAt: lock.meetingAt,
        notes: lock.notes,
        summary: dlSummary(person)
      })
    if (onClose) onClose()
  }

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"]
  const sub = {
    2: "ABC",
    3: "DEF",
    4: "GHI",
    5: "JKL",
    6: "MNO",
    7: "PQRS",
    8: "TUV",
    9: "WXYZ",
    0: "+"
  }

  if (minimized) {
    return (
      <button
        className="dlr-mini"
        onClick={() => setMinimized(false)}
        aria-label="Restore dialer"
      >
        <span
          className={
            "dlr-mini-av" +
            (phase === "live"
              ? " is-live"
              : phase === "ringing"
                ? " is-ring"
                : "")
          }
        >
          {dlInit(person.name)}
        </span>
        <span className="dlr-mini-tx">
          <span className="dlr-mini-nm">{person.name}</span>
          <span className="dlr-mini-st">
            {phase === "live"
              ? "On call · " + dlFmt(sec)
              : phase === "ringing"
                ? "Calling…"
                : phase === "ended"
                  ? dlOutcomeLabel(outcome)
                  : "New call"}
          </span>
        </span>
        {(phase === "ringing" || phase === "live") && (
          <span
            className="dlr-mini-hang"
            onClick={(e) => {
              e.stopPropagation()
              hangUp()
            }}
          >
            {I.phoneOff({ size: 15 })}
          </span>
        )}
        <span className="dlr-mini-up">{I.chevronUp({ size: 15 })}</span>
      </button>
    )
  }

  return (
    <React.Fragment>
      <div
        className="dlr"
        style={drag.style}
        role="dialog"
        aria-label="Call"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dlr-hd" onMouseDown={drag.onDragStart}>
          <span className="dlr-hd-t">
            {I.phone({ size: 13 })}{" "}
            {phase === "ended"
              ? dlOutcomeLabel(outcome)
              : phase === "live"
                ? "On call"
                : phase === "ringing"
                  ? "Calling…"
                  : "New call"}
          </span>
          <span className="dlr-hd-sp" />
          <button
            className="dlr-x"
            onClick={() => setMinimized(true)}
            aria-label="Minimize dialer"
          >
            {I.minus({ size: 16 })}
          </button>
          <button className="dlr-x" onClick={onClose} aria-label="Close dialer">
            {I.close({ size: 15 })}
          </button>
        </div>

        {phase === "compose" && (
          <div className="dlr-stage dlr-compose">
            <span className="dlr-av">{dlInit(person.name)}</span>
            <div className="dlr-name">{person.name}</div>
            <div className="dlr-role">
              {person.title}
              {person.account ? " · " + person.account.name : ""}
            </div>
            <button
              className="bdl-viewprofile"
              onClick={() => setShowProfile(true)}
            >
              {I.user({ size: 13 })} View full profile
            </button>

            {onFile && !editNum ? (
              <div className="dlr-numcard">
                <span className="dlr-numcard-lbl">
                  {I.phone({ size: 12 })} Number on file
                </span>
                <span className="dlr-numcard-val">{onFile}</span>
                <button
                  className="dlr-numswap"
                  onClick={() => {
                    setEditNum(true)
                    setNumber("")
                  }}
                >
                  Dial a different number
                </button>
              </div>
            ) : (
              <div className="dlr-numedit">
                <label className="dlr-numedit-lbl">
                  {onFile ? (
                    "Enter the number you'd like to dial"
                  ) : (
                    <>
                      <span className="dlr-nonum">
                        {I.info({ size: 12 })} No number on file
                      </span>
                      <span className="dlr-nonum-hint">
                        Enter one to dial this contact.
                      </span>
                    </>
                  )}
                </label>
                <input
                  className="dlr-numinput"
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  placeholder="+1 (555) 000-0000"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && dialable) setPhase("ringing")
                  }}
                />
                {onFile && (
                  <button
                    className="dlr-numswap"
                    onClick={() => {
                      setEditNum(false)
                      setNumber(onFile)
                    }}
                  >
                    Use number on file · {onFile}
                  </button>
                )}
              </div>
            )}

            <button
              className="dlr-callbtn"
              disabled={!dialable}
              onClick={() => setPhase("ringing")}
            >
              {I.phone({ size: 17 })} Call
            </button>
          </div>
        )}

        {(phase === "ringing" || phase === "live") && (
          <div className="dlr-stage">
            <span
              className={"dlr-av" + (phase === "ringing" ? " is-ring" : "")}
            >
              {dlInit(person.name)}
            </span>
            <div className="dlr-name">{person.name}</div>
            <div className="dlr-role">{person.title}</div>
            <button
              className="bdl-viewprofile"
              onClick={() => setShowProfile(true)}
            >
              {I.user({ size: 13 })} View full profile
            </button>
            <div className="dlr-num">
              {number}
              {pad ? " " + pad : ""}
            </div>
            <div className={"dlr-status is-" + phase}>
              {phase === "live" ? dlFmt(sec) : "Ringing…"}
            </div>

            {showPad && (
              <div className="dlr-pad">
                {keys.map((k) => (
                  <button
                    className="dlr-key"
                    key={k}
                    onClick={() => setPad((p) => p + k)}
                  >
                    <span className="dlr-key-n">{k}</span>
                    <span className="dlr-key-s">{sub[k] || " "}</span>
                  </button>
                ))}
                <button
                  className="dlr-padback"
                  onClick={() => setPad((p) => p.slice(0, -1))}
                  disabled={!pad}
                >
                  {I.backspace({ size: 15 })} Delete
                </button>
              </div>
            )}

            <div className="dlr-controls">
              <button
                className={"dlr-ctl" + (muted ? " is-on" : "")}
                onClick={() => setMuted((m) => !m)}
              >
                {muted ? I.micOff({ size: 18 }) : I.mic({ size: 18 })}
                <span>{muted ? "Unmute" : "Mute"}</span>
              </button>
              <button
                className={"dlr-ctl" + (showPad ? " is-on" : "")}
                onClick={() => setShowPad((s) => !s)}
              >
                {I.keypad({ size: 18 })}
                <span>Keypad</span>
              </button>
              <button
                className={"dlr-ctl" + (held ? " is-on" : "")}
                onClick={toggleHold}
              >
                {held ? I.play({ size: 18 }) : I.pause({ size: 18 })}
                <span>{held ? "Resume" : "Hold"}</span>
              </button>
            </div>

            <button className="dlr-hang" onClick={hangUp} aria-label="End call">
              {I.phoneOff({ size: 22 })}
            </button>
          </div>
        )}

        {phase === "ended" && (
          <div className="dlr-lockwrap">
            <CallLock4L
              person={person}
              durationSec={sec}
              outcome={outcome}
              log={lock}
              setLog={setLock}
              onProfile={() => setShowProfile(true)}
              variant="compact"
            />
            <div className="dlr-lockfoot">
              <button
                className="dlr-callbtn dlr-lockbtn"
                disabled={!lock.dispo}
                onClick={saveClose}
              >
                {I.check({ size: 15 })} Save &amp; close
              </button>
            </div>
          </div>
        )}
      </div>
      {showProfile && (
        <PersonDrawer person={person} onClose={() => setShowProfile(false)} />
      )}
    </React.Fragment>
  )
}

const BDL_OUTCOMES = [
  { id: "connected", label: "Connected", icon: "checkCircle" },
  { id: "voicemail", label: "Left voicemail", icon: "mic" },
  { id: "noanswer", label: "No answer", icon: "phoneOff" },
  { id: "skipped", label: "Skipped", icon: "chevR" }
]
const BDL_STLABEL = {
  queued: "Queued",
  connected: "Connected",
  voicemail: "Voicemail",
  noanswer: "No answer",
  skipped: "Skipped"
}
const BDL_TZ = [
  ["ET", "US Eastern", "US"],
  ["PT", "US Pacific", "US"],
  ["GMT", "UK / Europe", "UK"],
  ["IST", "India", "IN"],
  ["SGT", "Singapore", "SG"]
]
function bdlHash(s) {
  const str = String(s ?? "")
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0
  return Math.abs(h)
}
function bdlTz(id) {
  return BDL_TZ[bdlHash(id) % BDL_TZ.length][0]
}
function bdlCC(tz) {
  const row = BDL_TZ.find((t) => t[0] === tz)
  return row ? row[2] : "US"
}
function bdlPhone(id) {
  const areas = [212, 415, 646, 312, 617, 305, 720, 206]
  const h = bdlHash(id)
  return `+1 (${areas[h % areas.length]}) 555-${String(1000 + (h % 8999)).padStart(4, "0")}`
}

export function BulkDialer4L({
  people,
  rep = DEMO_REP,
  onClose,
  onLogCall,
  onEnrich
}) {
  const tokenInfo = useGlobalStore((s) => s.tokenInfo)
  const [dialerCfg, setDialerCfg] = useState({
    numbers: [],
    rotation: true,
    rotateN: 20,
    countryMatch: true
  })
  const [numOpen, setNumOpen] = useState(false)
  const persistCfg = (next) => setDialerCfg(next)

  useEffect(() => {
    if (!tokenInfo?.tenant_id) return
    let cancelled = false
    getVoiceNumbers(tokenInfo)
      .then((res) => {
        if (cancelled) return
        const pool = (res?.data?.numbers || []).map((n) => ({
          id: n.phone_number,
          number: n.phone_number,
          region: COUNTRY_LABEL[n.country_code] || n.country_code || "—",
          cc: n.country_code || "—",
          calls: n.day_count || 0,
          connects: "—",
          spam: n.status === "retired"
        }))
        if (pool.length) setDialerCfg((c) => ({ ...c, numbers: pool }))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [tokenInfo?.tenant_id])
  const patchCfg = (p) => persistCfg({ ...dialerCfg, ...p })
  const toggleSpam = (id) =>
    persistCfg({
      ...dialerCfg,
      numbers: dialerCfg.numbers.map((n) =>
        n.id === id ? { ...n, spam: !n.spam } : n
      )
    })
  const removeNum = (id) =>
    persistCfg({
      ...dialerCfg,
      numbers: dialerCfg.numbers.filter((n) => n.id !== id)
    })
  const addNum = () => {
    const cc = ["US", "GB", "IN"][dialerCfg.numbers.length % 3]
    const seed = {
      US: "+1 (415) 555-01",
      GB: "+44 20 7946 09",
      IN: "+91 22 4890 11"
    }[cc]
    persistCfg({
      ...dialerCfg,
      numbers: [
        ...dialerCfg.numbers,
        {
          id: "n" + dialerCfg.numbers.length,
          cc,
          number: seed + (10 + dialerCfg.numbers.length),
          region: {
            US: "United States",
            GB: "United Kingdom",
            IN: "Mumbai, India"
          }[cc],
          connects: "—",
          calls: 0
        }
      ]
    })
  }

  const [queue, setQueue] = useState(() =>
    people.map((p) => ({
      id: p.id,
      person: p,
      number: p.phone && /\d/.test(p.phone) ? p.phone : "",
      tz: bdlTz(p.id),
      status: "queued",
      durationSec: 0,
      dispo: "",
      meetingAt: null,
      notes: "",
      summary: null
    }))
  )
  const [order, setOrder] = useState([])
  const [oi, setOi] = useState(0)
  const [stage, setStage] = useState("setup")
  const [sec, setSec] = useState(0)
  const [count, setCount] = useState(3)
  const [muted, setMuted] = useState(false)
  const drag = useDragOffset()
  const [showPad, setShowPad] = useState(false)
  const [pad, setPad] = useState("")
  const [manual, setManual] = useState(false)
  const [maximized, setMaximized] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [profilePerson, setProfilePerson] = useState(null)
  const [log, setLog] = useState({ dispo: "", meetingAt: null, notes: "" })
  const [winFrom, setWinFrom] = useState("09:00")
  const [winTo, setWinTo] = useState("18:00")
  const tRef = useRef(null)
  const {
    status: devStatus,
    makeCall,
    logCall,
    watchCallStatus,
    endSession: completeDialerSession
  } = useVoiceDevice()
  const [outcome, setOutcome] = useState(null)
  const liveCallRef = useRef(null)
  const callSidRef = useRef(null)
  const answeredRef = useRef(false)
  const [held, setHeld, toggleHold] = useHoldToggle(tokenInfo, callSidRef)
  useEffect(() => {
    liveCallRef.current?.mute(muted)
  }, [muted])
  const tzSel = new Set(BDL_TZ.map((t) => t[0]))

  const total = queue.length
  const cursor = order.length ? order[oi] : 0
  const cur = queue[cursor] || queue[0]
  const done = order.slice(0, oi).length
  const nextCur = order[oi + 1] != null ? queue[order[oi + 1]] : null

  const clearTimers = () => {
    clearInterval(tRef.current)
    tRef.current = null
  }

  useEffect(() => {
    if (stage !== "countdown" || manual) return
    if (count <= 0) {
      setStage("ringing")
      return
    }
    const t = setTimeout(() => setCount((c) => c - 1), 720)
    return () => clearTimeout(t)
  }, [stage, count, manual])
  useEffect(() => {
    if (stage !== "ringing" || manual) return
    let cancelled = false
    let sim = null
    answeredRef.current = false
    ;(async () => {
      const to = cur && cur.number
      const params = {}
      if (dialerCfg.countryMatch && cur.person?.location_country)
        params.calleeCountry = cur.person.location_country
      if (dialerCfg.rotation && dialerCfg.rotateN)
        params.rotateAfter = String(dialerCfg.rotateN)
      const call =
        devStatus === "ready" && to
          ? await makeCall(
              to,
              Object.keys(params).length ? params : undefined,
              cur.person?.id,
              { start: winFrom, end: winTo }
            )
          : null
      if (cancelled) return
      if (!call) {
        sim = setTimeout(() => {
          answeredRef.current = true
          setStage("live")
        }, 2400)
        return
      }
      liveCallRef.current = call
      let stopWatching = () => {}
      call.on("accept", () => {
        callSidRef.current = call.parameters?.CallSid ?? null
        stopWatching = watchCallStatus(callSidRef.current, (status) => {
          setOutcome(status)
          if (status === "ANSWERED") {
            answeredRef.current = true
            setStage("live")
          }
        })
      })
      const end = () => {
        stopWatching()
        clearTimers()
        liveCallRef.current = null
        if (answeredRef.current) {
          setStage((s) => (s === "live" ? "log" : s))
        } else {
          patchCur({ status: "noanswer", durationSec: 0 })
          goTo(oi + 1)
        }
      }
      call.on("disconnect", end)
      call.on("cancel", end)
      call.on("reject", end)
      call.on("error", end)
    })()
    return () => {
      cancelled = true
      if (sim) clearTimeout(sim)
    }
  }, [stage, oi, manual, devStatus, watchCallStatus])
  useEffect(() => {
    if (stage !== "live") return
    tRef.current = setInterval(() => setSec((s) => s + 1), 1000)
    return () => clearInterval(tRef.current)
  }, [stage, oi])

  const patchCur = (patch) =>
    setQueue((qs) => qs.map((q, i) => (i === cursor ? { ...q, ...patch } : q)))

  const goTo = (nextOi, ord = order) => {
    clearTimers()
    if (nextOi >= ord.length) {
      completeDialerSession()
      setStage("done")
      return
    }
    setOi(nextOi)
    setSec(0)
    setOutcome(null)
    setMuted(false)
    setHeld(false)
    setShowPad(false)
    setPad("")
    setCount(3)
    setLog({ dispo: "", meetingAt: null, notes: "" })
    setShowProfile(false)
    setProfilePerson(null)
    setStage("countdown")
  }

  const included = queue.filter((q) => tzSel.has(q.tz) && q.number).length
  const needEnrich = queue.filter((q) => !q.number).length
  const enrichMissing = () => {
    setQueue((qs) =>
      qs.map((q) => (q.number ? q : { ...q, number: bdlPhone(q.id) }))
    )
    if (onEnrich) onEnrich()
  }
  const startSession = () => {
    const ord = []
    queue.forEach((q, i) => {
      if (tzSel.has(q.tz) && q.number) ord.push(i)
    })
    setQueue((qs) =>
      qs.map((q) =>
        tzSel.has(q.tz) && q.number ? q : { ...q, status: "skipped" }
      )
    )
    setOrder(ord)
    if (!ord.length) {
      completeDialerSession()
      setStage("done")
      return
    }
    goTo(0, ord)
  }

  const hangUp = () => {
    clearTimers()
    if (liveCallRef.current) {
      liveCallRef.current.disconnect()
      liveCallRef.current = null
    }
    setStage("log")
  }
  const noAnswer = (kind) => {
    clearTimers()
    if (liveCallRef.current) {
      liveCallRef.current.disconnect()
      liveCallRef.current = null
    }
    patchCur({ status: kind, durationSec: 0 })
    goTo(oi + 1)
  }
  const redial = () => {
    clearTimers()
    setSec(0)
    setOutcome(null)
    setCount(2)
    setStage("countdown")
  }

  const commitLog = () => {
    const dur = Math.max(sec, 1)
    const summary = dlSummary(cur.person)
    logCall(callSidRef.current, {
      disposition: log.dispo || undefined,
      notes: log.notes || undefined,
      meetingAt: log.meetingAt
    })
    callSidRef.current = null
    patchCur({
      status: "connected",
      durationSec: dur,
      dispo: log.dispo,
      meetingAt: log.meetingAt,
      notes: log.notes,
      summary
    })
    if (onLogCall)
      onLogCall(cur.person, {
        id: "call-" + Date.now(),
        type: "call",
        dir: "out",
        outcome: "connected",
        durationSec: dur,
        duration: dlFmt(dur),
        when: "Just now",
        date: "Today",
        rep,
        disposition: log.dispo,
        meetingAt: log.meetingAt,
        notes: log.notes,
        summary
      })
  }
  const logAndNext = () => {
    commitLog()
    goTo(oi + 1)
  }
  const logAndPause = () => {
    commitLog()
    clearTimers()
    setStage("paused")
  }
  const resume = () => goTo(oi + 1)
  const endSession = () => {
    clearTimers()
    completeDialerSession()
    setStage("done")
  }

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"]
  const sub = {
    2: "ABC",
    3: "DEF",
    4: "GHI",
    5: "JKL",
    6: "MNO",
    7: "PQRS",
    8: "TUV",
    9: "WXYZ",
    0: "+"
  }
  const av = (p) => p.initials || dlInit(p.name)
  const manualDialer = manual && (
    <Dialer4L
      person={{
        name: "Ad-hoc call",
        title: "Manual dial · mid-session",
        phone: ""
      }}
      rep={rep}
      onClose={() => setManual(false)}
    />
  )

  if (stage === "setup") {
    return (
      <div className="bdl-scrim" onMouseDown={() => onClose && onClose()}>
        <div
          className="bdl bdl-setupwrap"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Set up call session"
        >
          <div className="bdl-hd">
            <span className="bdl-hd-t">
              {I.phone({ size: 14 })} Set up call session
            </span>
            <span className="bdl-hd-sp" />
            <button
              className="dlr-x"
              onClick={() => onClose && onClose()}
              aria-label="Close"
            >
              {I.close({ size: 16 })}
            </button>
          </div>
          <div className="bdl-setup">
            <div className="bdl-setup-lead">
              <div className="bdl-setup-big">
                {included}
                <span> / {total}</span>
              </div>
              <div className="bdl-setup-cap">
                contacts ready to dial in this session
              </div>
            </div>

            <div className="bdl-sec">
              <div className="bdl-sec-t">
                {I.phone({ size: 12 })} Calling from
              </div>
              <button
                className="bdl-numconfirm bdl-numconfirm-btn"
                onClick={() => setNumOpen((o) => !o)}
              >
                <div className="bdl-numconfirm-main">
                  <span className="bdl-numconfirm-num">
                    {dialerCfg.countryMatch
                      ? "Auto — local presence"
                      : (dialerCfg.numbers[0] || {}).number}
                  </span>
                  <span className="bdl-numconfirm-reg">
                    {dialerCfg.numbers.length} lines
                    {dialerCfg.rotation
                      ? " · rotates after " + dialerCfg.rotateN + " calls"
                      : ""}
                    {dialerCfg.numbers.some((n) => n.spam)
                      ? " · " +
                        dialerCfg.numbers.filter((n) => n.spam).length +
                        " flagged"
                      : ""}
                  </span>
                </div>
                <span className="bdl-numchev">{I.chevD({ size: 16 })}</span>
              </button>

              {numOpen && (
                <div className="bdl-numpanel">
                  <label
                    className="bdl-numtoggle"
                    onClick={() =>
                      patchCfg({ countryMatch: !dialerCfg.countryMatch })
                    }
                  >
                    <span
                      className={
                        "bdl-numsw" + (dialerCfg.countryMatch ? " is-on" : "")
                      }
                    >
                      <span />
                    </span>
                    <span className="bdl-numtoggle-tx">
                      <b>Auto local presence</b>
                      <span>
                        Use a caller ID matching each contact's country
                      </span>
                    </span>
                  </label>

                  <div className="bdl-numlist">
                    {dialerCfg.numbers.map((n) => (
                      <div
                        className={"bdl-numitem" + (n.spam ? " is-spam" : "")}
                        key={n.id}
                      >
                        <span className="bdl-numflag">{n.cc}</span>
                        <span className="bdl-numitem-main">
                          <span className="bdl-numitem-num">
                            {n.number}
                            {n.spam ? (
                              <span className="bdl-spamtag">
                                {I.alert({ size: 10 })} Spam-flagged
                              </span>
                            ) : null}
                          </span>
                          <span className="bdl-numitem-meta">
                            {n.connects != null
                              ? n.connects + " connects · "
                              : ""}
                            {n.calls || 0} calls · {n.region}
                          </span>
                        </span>
                        <button
                          className={
                            "bdl-numflagbtn" + (n.spam ? " is-on" : "")
                          }
                          onClick={() => toggleSpam(n.id)}
                          title={n.spam ? "Clear spam flag" : "Mark as spam"}
                        >
                          {I.alert({ size: 13 })}
                        </button>
                        <button
                          className="bdl-numdel"
                          onClick={() => removeNum(n.id)}
                          title="Remove"
                        >
                          {I.close({ size: 13 })}
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="bdl-numadd" onClick={addNum}>
                    {I.plus({ size: 13 })} Add a new number
                  </button>

                  <div className="bdl-numrot">
                    <label
                      className="bdl-numtoggle"
                      onClick={() =>
                        patchCfg({ rotation: !dialerCfg.rotation })
                      }
                    >
                      <span
                        className={
                          "bdl-numsw" + (dialerCfg.rotation ? " is-on" : "")
                        }
                      >
                        <span />
                      </span>
                      <span className="bdl-numtoggle-tx">
                        <b>Number rotation</b>
                        <span>Spread volume across lines to avoid overuse</span>
                      </span>
                    </label>
                    {dialerCfg.rotation && (
                      <div className="bdl-rotslider">
                        <span className="bdl-rotlbl">
                          Rotate after <b>{dialerCfg.rotateN}</b> calls / number
                        </span>
                        <input
                          type="range"
                          min="10"
                          max="50"
                          step="5"
                          value={dialerCfg.rotateN || 20}
                          onChange={(e) =>
                            patchCfg({ rotateN: +e.target.value })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bdl-sec">
              <div className="bdl-sec-t">
                {I.clock({ size: 12 })} Calling hours{" "}
                <span className="bdl-sec-hint">contact's local time</span>
              </div>
              <div className="bdl-winrow">
                <input
                  type="time"
                  className="bdl-wintime"
                  value={winFrom}
                  onChange={(e) => setWinFrom(e.target.value)}
                />
                <span className="bdl-winsep">to</span>
                <input
                  type="time"
                  className="bdl-wintime"
                  value={winTo}
                  onChange={(e) => setWinTo(e.target.value)}
                />
              </div>
              <div className="bdl-tznote">
                Calls fire only when it's between {winFrom} and {winTo} for the
                contact. Outside the window they wait in queue.
              </div>
            </div>

            {needEnrich > 0 && (
              <div className="bdl-enrich">
                <span className="bdl-enrich-ic">{I.info({ size: 14 })}</span>
                <span className="bdl-enrich-tx">
                  <b>{needEnrich}</b>{" "}
                  {needEnrich === 1 ? "contact has" : "contacts have"} no phone
                  number yet.
                </span>
                <button className="bdl-enrich-btn" onClick={enrichMissing}>
                  {I.sparkle({ size: 12 })} Enrich {needEnrich}
                </button>
              </div>
            )}
          </div>
          <div className="bdl-foot">
            <button className="btn" onClick={() => onClose && onClose()}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={included === 0}
              onClick={startSession}
            >
              {I.play({ size: 13 })} Start session · {included}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (stage === "done") {
    const tally = {}
    queue.forEach((q) => {
      tally[q.status] = (tally[q.status] || 0) + 1
    })
    const reached = tally.connected || 0
    const called = queue.filter(
      (q) =>
        q.status === "connected" ||
        q.status === "voicemail" ||
        q.status === "noanswer"
    ).length
    const rate = called ? Math.round((reached / called) * 100) : 0
    const connected = queue.filter((q) => q.status === "connected")
    return (
      <div className="bdl-scrim" onMouseDown={() => onClose && onClose()}>
        <div
          className="bdl bdl-recap"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="bdl-hd">
            <span className="bdl-hd-t">
              {I.checkCircle({ size: 15 })} Session complete
            </span>
            <button
              className="dlr-x"
              onClick={() => onClose && onClose()}
              aria-label="Close"
            >
              {I.close({ size: 16 })}
            </button>
          </div>
          <div className="bdl-recap-body">
            <div className="bdl-recap-head">
              <div className="bdl-recap-big">
                {reached}
                <span>/{called}</span>
              </div>
              <div className="bdl-recap-cap">
                connected live · {rate}% connect rate
                {reached > 0 ? " — logged to each contact's timeline" : ""}
              </div>
            </div>
            <div className="bdl-recap-stats">
              {BDL_OUTCOMES.map((o) => (
                <div className="bdl-stat" key={o.id}>
                  <span className={"bdl-stat-ic is-" + o.id}>
                    {I[o.icon]({ size: 15 })}
                  </span>
                  <span className="bdl-stat-n">{tally[o.id] || 0}</span>
                  <span className="bdl-stat-l">{o.label}</span>
                </div>
              ))}
            </div>
            {connected.length > 0 && (
              <div className="bdl-summaries">
                <div className="bdl-sec-t">
                  {I.sparkle({ size: 12 })} Call summaries · auto-captured by
                  OrbitShift
                </div>
                {connected.map((q) => (
                  <div className="bdl-sumcard" key={q.id}>
                    <div className="bdl-sumcard-hd">
                      <span className="dlr-av sm">{av(q.person)}</span>
                      <div className="bdl-sumcard-id">
                        <span className="bdl-rn">{q.person.name}</span>
                        <span className="bdl-sumcard-meta">
                          {dlFmt(q.durationSec)}
                          {q.dispo ? " · " + CALL_DISPO_LABEL[q.dispo] : ""}
                          {q.dispo === "meeting" &&
                          q.meetingAt &&
                          q.meetingAt.date
                            ? " · " + clkFmtMeeting(q.meetingAt)
                            : ""}
                        </span>
                      </div>
                      {q.dispo && (
                        <span
                          className={"clk-chip is-" + CALL_DISPO_CLS[q.dispo]}
                        >
                          {CALL_DISPO_LABEL[q.dispo]}
                        </span>
                      )}
                    </div>
                    <p className="bdl-sumcard-tldr">
                      {q.summary ? q.summary.tldr : ""}
                    </p>
                    {q.notes && (
                      <div className="bdl-sumcard-note">
                        {I.edit({ size: 11 })} {q.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bdl-foot">
            <button
              className="btn btn-primary"
              onClick={() => onClose && onClose()}
            >
              {I.check({ size: 14 })} Done
            </button>
          </div>
        </div>
      </div>
    )
  }

  const liveTally = {}
  queue.forEach((q) => {
    liveTally[q.status] = (liveTally[q.status] || 0) + 1
  })
  const liveConnected = liveTally.connected || 0
  const liveVm = liveTally.voicemail || 0
  const liveMeetings = queue.filter(
    (q) => q.status === "connected" && q.dispo === "meeting"
  ).length
  const liveCalled = queue.filter((q) =>
    ["connected", "voicemail", "noanswer"].includes(q.status)
  ).length
  const liveRate = liveCalled
    ? Math.round((liveConnected / liveCalled) * 100)
    : 0
  const liveNums = dialerCfg.numbers.filter((n) => !n.spam)
  const fromNum = dialerCfg.countryMatch
    ? liveNums.find((n) => n.cc === bdlCC(cur.tz)) ||
      liveNums[0] ||
      dialerCfg.numbers[0]
    : liveNums[0] || dialerCfg.numbers[0]
  return (
    <React.Fragment>
      <div className="bdl-scrim" onMouseDown={() => onClose && onClose()}>
        <div
          className={
            "bdl" +
            (maximized ? " bdl-max" : "") +
            (collapsed ? " bdl-min" : "")
          }
          style={drag.style}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Power dialer session"
        >
          <div className="bdl-hd" onMouseDown={drag.onDragStart}>
            <span className="bdl-hd-t">
              {I.phone({ size: 14 })}{" "}
              {dialerCfg.countryMatch ? "Local presence" : "Session"} ·{" "}
              {(fromNum || {}).number}
            </span>
            <div className="bdl-prog">
              <span>
                {done} / {order.length} called
              </span>
              <span className="bdl-progbar">
                <i style={{ width: (done / order.length) * 100 + "%" }} />
              </span>
            </div>
            <div className="bdl-hdctl">
              <button
                className="bdl-hdbtn"
                onClick={() => setManual(true)}
                title="Manual ad-hoc dial"
              >
                {I.keypad({ size: 14 })} Manual
              </button>
              <button
                className="bdl-hdbtn is-end"
                onClick={endSession}
                title="End session"
              >
                {I.phoneOff({ size: 14 })} End
              </button>
            </div>
            <button
              className="dlr-x"
              onClick={() => setCollapsed((c) => !c)}
              aria-label="Minimize"
              title="Minimize"
            >
              {I.minus({ size: 16 })}
            </button>
            <button
              className="dlr-x"
              onClick={() => setMaximized((m) => !m)}
              aria-label="Maximize"
              title="Maximize"
            >
              {I.maximize({ size: 15 })}
            </button>
            <button
              className="dlr-x"
              onClick={() => onClose && onClose()}
              aria-label="Close"
            >
              {I.close({ size: 16 })}
            </button>
          </div>

          <div className="bdl-dash">
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{done}</span>
              <span className="bdl-dashl">Dialed</span>
            </div>
            <div className="bdl-dashstat is-good">
              <span className="bdl-dashn">{liveConnected}</span>
              <span className="bdl-dashl">Connected</span>
            </div>
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{liveVm}</span>
              <span className="bdl-dashl">Voicemail</span>
            </div>
            <div className="bdl-dashstat is-accent">
              <span className="bdl-dashn">{liveMeetings}</span>
              <span className="bdl-dashl">Meetings</span>
            </div>
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{liveRate}%</span>
              <span className="bdl-dashl">Connect rate</span>
            </div>
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{order.length - done}</span>
              <span className="bdl-dashl">Remaining</span>
            </div>
          </div>

          <div className="bdl-body">
            <div className="bdl-rail">
              <div className="bdl-rail-t">Queue · {order.length}</div>
              {order.map((qi, i) => {
                const q = queue[qi]
                return (
                  <button
                    className={
                      "bdl-rail-row" +
                      (i === oi ? " is-cur" : "") +
                      (i < oi ? " is-past" : "")
                    }
                    key={q.id}
                    onClick={() => {
                      setProfilePerson(q.person)
                      setShowProfile(true)
                    }}
                    title={"Open " + q.person.name + "'s details"}
                  >
                    <span
                      className={
                        "bdl-dot is-" + (i === oi ? "active" : q.status)
                      }
                    />
                    <span className="bdl-rail-main">
                      <span className="bdl-rn">{q.person.name}</span>
                      <span className="bdl-rt">
                        {q.person.account
                          ? q.person.account.name
                          : q.person.title}
                      </span>
                    </span>
                    <span className="bdl-rail-open">
                      {I.external({ size: 13 })}
                    </span>
                    <span className="bdl-rst">
                      {i === oi ? "Now" : BDL_STLABEL[q.status]}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className={"bdl-stage" + (stage === "log" ? " is-log" : "")}>
              {stage === "log" ? (
                <div className="bdl-logbody">
                  <CallLock4L
                    person={cur.person}
                    durationSec={sec}
                    outcome={outcome}
                    log={log}
                    setLog={setLog}
                    onProfile={() => {
                      setProfilePerson(cur.person)
                      setShowProfile(true)
                    }}
                    onRedial={redial}
                    variant="wide"
                  />
                  <div className="bdl-logbtns">
                    <button className="bdl-skip" onClick={() => goTo(oi + 1)}>
                      Skip {I.chevR({ size: 14 })}
                    </button>
                    <button
                      className="bdl-logsecond"
                      disabled={!log.dispo}
                      onClick={logAndPause}
                    >
                      {I.check({ size: 13 })} Log &amp; pause
                    </button>
                    <button
                      className="dlr-callbtn bdl-lognext"
                      disabled={!log.dispo}
                      onClick={logAndNext}
                    >
                      {I.check({ size: 15 })} Log &amp; dial next{" "}
                      {nextCur ? "· " + nextCur.person.name.split(" ")[0] : ""}
                    </button>
                  </div>
                </div>
              ) : stage === "paused" ? (
                <React.Fragment>
                  <span className="bdl-pausebadge">
                    {I.pause({ size: 22 })}
                  </span>
                  <div className="dlr-name">Session paused</div>
                  <div className="dlr-role">
                    {done} of {order.length} contacts called ·{" "}
                    {order.length - done} remaining
                  </div>
                  <div className="bdl-composebtns">
                    {nextCur && (
                      <button className="dlr-callbtn" onClick={resume}>
                        {I.play({ size: 16 })} Resume ·{" "}
                        {nextCur.person.name.split(" ")[0]}
                      </button>
                    )}
                    <button className="bdl-skip" onClick={endSession}>
                      End session {I.chevR({ size: 15 })}
                    </button>
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <span
                    className={
                      "dlr-av" + (stage === "ringing" ? " is-ring" : "")
                    }
                  >
                    {av(cur.person)}
                  </span>
                  <div className="dlr-name">{cur.person.name}</div>
                  <div className="dlr-role">
                    {cur.person.title}
                    {cur.person.account ? " · " + cur.person.account.name : ""}
                  </div>
                  <button
                    className="bdl-viewprofile"
                    onClick={() => {
                      setProfilePerson(cur.person)
                      setShowProfile(true)
                    }}
                  >
                    {I.user({ size: 13 })} View full profile
                  </button>

                  {stage === "countdown" && (
                    <React.Fragment>
                      <div className="bdl-count">
                        {I.phone({ size: 15 })} Dialing in <b>{count}</b>
                      </div>
                      <div className="dlr-num">{cur.number}</div>
                      <div className="bdl-composebtns">
                        <button
                          className="dlr-callbtn"
                          onClick={() => {
                            setCount(0)
                            setStage("ringing")
                          }}
                        >
                          {I.phone({ size: 16 })} Dial now
                        </button>
                        <div className="bdl-countctl">
                          <button
                            className="bdl-skip"
                            onClick={() => setStage("paused")}
                          >
                            {I.pause({ size: 13 })} Pause
                          </button>
                          <button
                            className="bdl-skip"
                            onClick={() => noAnswer("skipped")}
                          >
                            Skip {I.chevR({ size: 14 })}
                          </button>
                        </div>
                      </div>
                    </React.Fragment>
                  )}

                  {(stage === "ringing" || stage === "live") && (
                    <React.Fragment>
                      <div className="dlr-num">
                        {cur.number}
                        {pad ? " " + pad : ""}
                      </div>
                      <div className={"dlr-status is-" + stage}>
                        {stage === "live" ? dlFmt(sec) : "Ringing…"}
                      </div>
                      <div className="dlr-controls">
                        <button
                          className={"dlr-ctl" + (muted ? " is-on" : "")}
                          onClick={() => setMuted((m) => !m)}
                        >
                          {muted ? I.micOff({ size: 18 }) : I.mic({ size: 18 })}
                          <span>{muted ? "Unmute" : "Mute"}</span>
                        </button>
                        <button
                          className={"dlr-ctl" + (showPad ? " is-on" : "")}
                          onClick={() => setShowPad((s) => !s)}
                        >
                          {I.keypad({ size: 18 })}
                          <span>Keypad</span>
                        </button>
                        <button
                          className={"dlr-ctl" + (held ? " is-on" : "")}
                          onClick={toggleHold}
                        >
                          {held ? I.play({ size: 18 }) : I.pause({ size: 18 })}
                          <span>{held ? "Resume" : "Hold"}</span>
                        </button>
                      </div>
                      <div className="bdl-callrow">
                        {stage === "ringing" && (
                          <button
                            className="bdl-noanswer"
                            onClick={() => noAnswer("noanswer")}
                          >
                            {I.phoneOff({ size: 13 })} No answer
                          </button>
                        )}
                        <button
                          className="dlr-hang"
                          onClick={hangUp}
                          aria-label="End call"
                        >
                          {I.phoneOff({ size: 22 })}
                        </button>
                        {stage === "ringing" && (
                          <button
                            className="bdl-noanswer"
                            onClick={() => noAnswer("voicemail")}
                          >
                            {I.mic({ size: 13 })} Voicemail
                          </button>
                        )}
                      </div>
                      {nextCur && (
                        <div className="bdl-upnext">
                          Up next · {nextCur.person.name}
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
            </div>
            {showPad && (stage === "ringing" || stage === "live") && (
              <div className="dlr-pad dlr-pad-drawer">
                {keys.map((k) => (
                  <button
                    className="dlr-key"
                    key={k}
                    onClick={() => setPad((p) => p + k)}
                  >
                    <span className="dlr-key-n">{k}</span>
                    <span className="dlr-key-s">{sub[k] || " "}</span>
                  </button>
                ))}
                <button
                  className="dlr-padback"
                  onClick={() => setPad((p) => p.slice(0, -1))}
                  disabled={!pad}
                >
                  {I.backspace({ size: 15 })} Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {manualDialer}
      {showProfile && (
        <PersonDrawer
          person={profilePerson || cur.person}
          onClose={() => setShowProfile(false)}
        />
      )}
    </React.Fragment>
  )
}

export function PeopleDialerDemo() {
  const [tab, setTab] = useState("list")
  const list =
    DEMO_LISTS.find(
      (l) => l.id === (tab === "list" ? "priority-champions" : "recommended")
    ) || DEMO_LISTS[0]
  const [selected, setSelected] = useState(() => new Set())
  const startBulk = useVoiceSessionStore((s) => s.startBulk)
  const startSingle = useVoiceSessionStore((s) => s.startSingle)

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(id)) {
        n.delete(id)
      } else {
        n.add(id)
      }
      return n
    })
  const chosen = list.people.filter((p) => selected.has(p.id))

  return (
    <div className="voice-dialer-root voice-dialer-page">
      <div className="pdemo">
        <h1>People</h1>
        <p className="pdemo-sub">
          Select contacts and power-dial through them, or call one directly.
          (Demo data — the People workspace is built separately.)
        </p>

        <div className="pdemo-tabs">
          <button
            className={"pdemo-tab" + (tab === "list" ? " is-on" : "")}
            onClick={() => {
              setTab("list")
              setSelected(new Set())
            }}
          >
            People list
          </button>
          <button
            className={"pdemo-tab" + (tab === "rec" ? " is-on" : "")}
            onClick={() => {
              setTab("rec")
              setSelected(new Set())
            }}
          >
            Recommended people
          </button>
        </div>

        <div className="pdemo-listhd">
          <span className="pdemo-listnm">
            {list.kind === "priority"
              ? I.crown({ size: 15 })
              : I.sparkle({ size: 15 })}{" "}
            {list.name}
          </span>
          <span className="pdemo-count">
            {list.people.length} contacts · {chosen.length} selected
          </span>
          <span className="pdemo-bar">
            <button
              className="pdemo-power"
              disabled={chosen.length === 0}
              onClick={() => startBulk(chosen)}
            >
              {I.phone({ size: 14 })} Power Dial · {chosen.length}
            </button>
          </span>
        </div>

        <div className="pdemo-list">
          {list.people.map((p) => (
            <div
              key={p.id}
              className={"pdemo-row" + (selected.has(p.id) ? " is-sel" : "")}
              onClick={() => toggle(p.id)}
            >
              <span className="pdemo-check">
                {selected.has(p.id) ? I.check({ size: 13 }) : null}
              </span>
              <span className="pdemo-av">{dlInit(p.name)}</span>
              <span className="pdemo-main">
                <span className="pdemo-nm">{p.name}</span>
                <span className="pdemo-role">
                  {p.title} · {p.account.name}
                </span>
              </span>
              {p.phone ? (
                <span className="pdemo-phone">{p.phone}</span>
              ) : (
                <span className="pdemo-nophone">No number</span>
              )}
              <button
                className="pdemo-call"
                onClick={(e) => {
                  e.stopPropagation()
                  startSingle(p)
                }}
              >
                {I.phone({ size: 12 })} Call
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
